import { useMemo } from 'react';

import { useReferenceAssistantGateway } from './ReferenceAssistantGatewayProvider';
import { getStatementReferencesV2QueryOptions } from '../api/queryOptions';
import type { StatementReferenceState } from '../model/statementReferenceState';

import { useUserScopedQuery } from '@/hooks/useUserScopedQuery';

/**
 * A conversation's statement references keyed by message SID, read through
 * the gateway in context. Empty until the first fetch resolves, which draws
 * no statements bubble rather than a stale one.
 */
export function useStatementReferences(conversationId: string, { enabled = true } = {}) {
  const gateway = useReferenceAssistantGateway();

  const { data } = useUserScopedQuery(
    getStatementReferencesV2QueryOptions,
    [conversationId, gateway],
    { enabled }
  );

  return useMemo<ReadonlyMap<string, StatementReferenceState>>(
    () => new Map((data ?? []).map((state) => [state.messageSid, state])),
    [data]
  );
}

/** One message's statement references, or `undefined` when it has none. */
export function useStatementReferenceState(
  conversationId: string,
  messageSid: string | undefined,
  options: { enabled?: boolean } = {}
): StatementReferenceState | undefined {
  const states = useStatementReferences(conversationId, options);

  return messageSid ? states.get(messageSid) : undefined;
}
